import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('authUser');
    }
    return Promise.reject(error.response?.data || { success: false, message: error.message || 'Network error' });
  }
);

export const authAPI = {
  register: async (userData: any) => {
    try {
      const response = await api.post('/users/register', userData);
      return response.data;
    } catch (error: any) {
      return error.success !== undefined ? error : { success: false, message: error.message || 'Registration failed' };
    }
  },

  login: async (email: string, password: string) => {
    try {
      const response = await api.post('/users/login', { email: email.trim().toLowerCase(), password });
      return response.data;
    } catch (error: any) {
      return error.success !== undefined ? error : { success: false, message: error.message || 'Login failed' };
    }
  },

  getUserProfile: async () => {
    try {
      const response = await api.get('/users/profile');
      return response.data;
    } catch (error: any) {
      return { success: false, message: error.message || 'Failed to fetch profile' };
    }
  },

  updateUserProfile: async (data: any) => {
    try {
      const response = await api.put('/users/profile', data);
      return response.data;
    } catch (error: any) {
      return { success: false, message: error.message || 'Failed to update profile' };
    }
  },

  forgotPassword: async (email: string) => {
    try {
      const response = await api.post('/users/forgot-password', { email: email.trim().toLowerCase() });
      return response.data;
    } catch (error: any) {
      return error.success !== undefined ? error : { success: false, message: error.message || 'Request failed' };
    }
  },

  resetPassword: async (token: string, password: string) => {
    try {
      const response = await api.post('/users/reset-password', { token, password });
      return response.data;
    } catch (error: any) {
      return error.success !== undefined ? error : { success: false, message: error.message || 'Reset failed' };
    }
  },
};

export const workoutAPI = {
  getWorkoutPlans: async () => {
    const response = await api.get('/workouts');
    return response.data;
  },
  createWorkoutPlan: async (plan: any) => {
    const response = await api.post('/workouts', plan);
    return response.data;
  },
  updateWorkoutPlan: async (id: string, plan: any) => {
    const response = await api.put(`/workouts/${id}`, plan);
    return response.data;
  },
  deleteWorkoutPlan: async (id: string) => {
    const response = await api.delete(`/workouts/${id}`);
    return response.data;
  },
  generateWorkoutPlan: async () => {
    const response = await api.post('/plans/generate/workout', {}, { timeout: 60000 });
    return response.data;
  },
};

export const dietAPI = {
  getDietPlans: async () => {
    const response = await api.get('/diets');
    return response.data;
  },
  createDietPlan: async (plan: any) => {
    const response = await api.post('/diets', plan);
    return response.data;
  },
  updateDietPlan: async (id: string, plan: any) => {
    const response = await api.put(`/diets/${id}`, plan);
    return response.data;
  },
  deleteDietPlan: async (id: string) => {
    const response = await api.delete(`/diets/${id}`);
    return response.data;
  },
  generateDietPlan: async () => {
    const response = await api.post('/plans/generate/diet', {}, { timeout: 60000 });
    return response.data;
  },
};

export const progressAPI = {
  getWorkoutLogs: async (userId: string) => {
    try {
      const response = await api.get(`/progress/${userId}/workout-logs`);
      return response.data;
    } catch { return { success: false, logs: [] }; }
  },
  createWorkoutLog: async (userId: string, data: any) => {
    const response = await api.post(`/progress/${userId}/workout-logs`, data);
    return response.data;
  },
  updateWorkoutLog: async (userId: string, logId: string, data: any) => {
    const response = await api.put(`/progress/${userId}/workout-logs/${logId}`, data);
    return response.data;
  },
  deleteWorkoutLog: async (userId: string, logId: string) => {
    const response = await api.delete(`/progress/${userId}/workout-logs/${logId}`);
    return response.data;
  },

  getBodyMeasurements: async (userId: string) => {
    try {
      const response = await api.get(`/progress/${userId}/body-measurements`);
      return response.data;
    } catch { return { success: false, measurements: [] }; }
  },
  createBodyMeasurement: async (userId: string, data: any) => {
    const response = await api.post(`/progress/${userId}/body-measurements`, data);
    return response.data;
  },
  updateBodyMeasurement: async (userId: string, id: string, data: any) => {
    const response = await api.put(`/progress/${userId}/body-measurements/${id}`, data);
    return response.data;
  },
  deleteBodyMeasurement: async (userId: string, id: string) => {
    const response = await api.delete(`/progress/${userId}/body-measurements/${id}`);
    return response.data;
  },

  getDietLogs: async (userId: string) => {
    try {
      const response = await api.get(`/progress/${userId}/diet-logs`);
      return response.data;
    } catch { return { success: false, logs: [] }; }
  },
  createDietLog: async (userId: string, data: any) => {
    const response = await api.post(`/progress/${userId}/diet-logs`, data);
    return response.data;
  },
  updateDietLog: async (userId: string, logId: string, data: any) => {
    const response = await api.put(`/progress/${userId}/diet-logs/${logId}`, data);
    return response.data;
  },
  deleteDietLog: async (userId: string, logId: string) => {
    const response = await api.delete(`/progress/${userId}/diet-logs/${logId}`);
    return response.data;
  },
};

export const chatAPI = {
  sendMessage: async (message: string) => {
    const response = await api.post('/chat/message', { message }, { timeout: 60000 });
    return response.data;
  },
  getHistory: async () => {
    try {
      const response = await api.get('/chat/history');
      return response.data;
    } catch { return { success: false, messages: [] }; }
  },
  clearHistory: async () => {
    const response = await api.delete('/chat/history');
    return response.data;
  },
};

export const stripeAPI = {
  createCheckout: async (priceId: string) => {
    const response = await api.post('/stripe/create-checkout', { priceId });
    return response.data;
  },
  getPortal: async () => {
    const response = await api.post('/stripe/portal');
    return response.data;
  },
  getStatus: async () => {
    try {
      const response = await api.get('/stripe/status');
      return response.data;
    } catch { return { success: false, subscription: { status: 'none' } }; }
  },
};

export default {
  auth: authAPI,
  workout: workoutAPI,
  diet: dietAPI,
  progress: progressAPI,
  chat: chatAPI,
  stripe: stripeAPI,
  axiosInstance: api,
};
